import {
  ArrowRightFromSquare,
  ArrowUpRightFromSquare,
  Bars,
  ChartColumn,
  ClockArrowRotateLeft,
  Gear,
  Globe,
  Key,
  Layers,
  LayoutCells,
  ListUl,
  Moon,
  PaperPlane,
  Person,
  Persons,
  Server,
  Sun,
} from "@gravity-ui/icons";
import { Avatar, Button, Drawer, Dropdown, Label, Tooltip } from "@heroui/react";
import { type ComponentType, type ReactNode, type SVGProps, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation, useNavigate } from "react-router";
import { LANGUAGES } from "../i18n";
import { APP_NAME, PUBLIC_API_URL } from "../lib/config";
import { useLogout, useMe } from "../lib/queries";
import { useTheme } from "../lib/theme";

type Icon = ComponentType<SVGProps<SVGSVGElement>>;
type NavEntry = { to: string; icon: Icon; label: string; group: string; end?: boolean };

function NavItem({ entry }: { entry: NavEntry }) {
  const { to, icon: IconCmp, label, end } = entry;
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        [
          "flex shrink-0 items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors",
          "outline-none focus-visible:ring-2 focus-visible:ring-accent",
          isActive
            ? "bg-accent-soft text-accent-soft-foreground"
            : "text-muted hover:bg-default hover:text-foreground",
        ].join(" ")
      }
    >
      <IconCmp className="size-4 shrink-0" />
      {label}
    </NavLink>
  );
}

function ExternalLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="flex items-center justify-between gap-3 rounded-xl px-3 py-2 text-sm text-muted outline-none hover:bg-default hover:text-foreground focus-visible:ring-2 focus-visible:ring-accent"
    >
      {children}
      <ArrowUpRightFromSquare className="size-3.5 shrink-0" />
    </a>
  );
}

export function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <div className="flex items-center gap-3">
      <img src="/favicon.svg" alt="" className="size-8" />
      <div className="leading-tight">
        <div className="text-sm font-semibold whitespace-nowrap">{APP_NAME}</div>
        {compact ? null : <div className="text-xs whitespace-nowrap text-muted">Notification gateway</div>}
      </div>
    </div>
  );
}

/** Language and theme switches. Used in the app header and on the sign-in page. */
export function PreferenceControls() {
  const { t, i18n } = useTranslation();
  const { theme, toggle } = useTheme();
  return (
    <div className="flex items-center gap-1">
      <Dropdown>
        <Button variant="ghost" size="sm" aria-label={t("nav.language")}>
          <Globe className="size-4" />
          <span className="uppercase">{i18n.resolvedLanguage}</span>
        </Button>
        <Dropdown.Popover>
          <Dropdown.Menu onAction={(key) => void i18n.changeLanguage(String(key))}>
            {LANGUAGES.map((l) => (
              <Dropdown.Item key={l.code} id={l.code} textValue={l.label}>
                <Label>{l.label}</Label>
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown.Popover>
      </Dropdown>
      <Tooltip delay={300}>
        <Button variant="ghost" size="sm" isIconOnly aria-label={t("nav.theme")} onPress={toggle}>
          {theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
        </Button>
        <Tooltip.Content>
          <p>{theme === "dark" ? t("nav.light") : t("nav.dark")}</p>
        </Tooltip.Content>
      </Tooltip>
    </div>
  );
}

function useNavEntries(): NavEntry[] {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const monitor = t("nav.groupMonitor");
  const messaging = t("nav.groupMessaging");
  const setup = t("nav.groupSetup");
  const admin = t("nav.groupAdmin");
  const entries: Array<NavEntry & { show?: boolean }> = [
    { to: "/", icon: ChartColumn, label: t("nav.overview"), group: monitor, end: true },
    { to: "/logs", icon: ListUl, label: t("nav.logs"), group: monitor },
    { to: "/customers", icon: Persons, label: t("nav.customers"), group: monitor },
    { to: "/send", icon: PaperPlane, label: t("nav.send"), group: messaging, show: me?.role !== "viewer" },
    { to: "/apps", icon: LayoutCells, label: t("nav.apps"), group: setup },
    { to: "/credential-profiles", icon: Layers, label: t("nav.profiles"), group: setup },
    { to: "/credentials", icon: Key, label: t("nav.credentials"), group: setup },
    { to: "/providers", icon: Server, label: t("nav.providers"), group: setup },
    { to: "/audit", icon: ClockArrowRotateLeft, label: t("nav.audit"), group: admin },
    { to: "/settings", icon: Gear, label: t("nav.settings"), group: admin, show: Boolean(me?.is_superadmin) },
  ];
  return entries.filter((e) => e.show !== false);
}

function Navigation() {
  const { t } = useTranslation();
  const entries = useNavEntries();
  const groups = [...new Set(entries.map((e) => e.group))];
  return (
    <div className="flex h-full flex-col gap-8">
      <nav className="flex flex-col gap-6" aria-label={APP_NAME}>
        {groups.map((group) => (
          <div key={group} className="flex flex-col gap-1">
            <div className="px-3 pb-1 text-[11px] font-medium tracking-wider text-muted uppercase">
              {group}
            </div>
            {entries
              .filter((e) => e.group === group)
              .map((e) => (
                <NavItem key={e.to} entry={e} />
              ))}
          </div>
        ))}
      </nav>
      <div className="mt-auto flex flex-col gap-1">
        <ExternalLink href={`${PUBLIC_API_URL}/docs`}>{t("nav.apiDocs")}</ExternalLink>
        <ExternalLink href={`${PUBLIC_API_URL}/admin/docs`}>{t("nav.adminDocs")}</ExternalLink>
      </div>
    </div>
  );
}

function UserMenu() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const logout = useLogout();
  const navigate = useNavigate();
  if (!me) return null;
  return (
    <Dropdown>
      <Button variant="ghost" size="sm" aria-label={me.email} className="gap-2 pl-1">
        <Avatar size="sm">
          <Avatar.Fallback>{me.name.slice(0, 2).toUpperCase()}</Avatar.Fallback>
        </Avatar>
        <span className="hidden max-w-40 truncate sm:inline">{me.name}</span>
      </Button>
      <Dropdown.Popover className="min-w-60">
        <div className="flex flex-col gap-0.5 px-3 pt-3 pb-2">
          <div className="text-sm font-medium">{me.name}</div>
          <div className="text-xs text-muted">{me.email}</div>
          <div className="text-xs text-muted">
            {me.org.name} · {t(`account.roles.${me.role}`)}
            {me.is_superadmin ? ` · ${t("nav.superadmin")}` : ""}
          </div>
        </div>
        <Dropdown.Menu
          onAction={(key) => {
            if (key === "account") navigate("/account");
            if (key === "logout") {
              logout.mutate(undefined, { onSettled: () => navigate("/login", { replace: true }) });
            }
          }}
        >
          <Dropdown.Item id="account" textValue={t("nav.account")}>
            <Person className="size-4" />
            <Label>{t("nav.account")}</Label>
          </Dropdown.Item>
          <Dropdown.Item id="logout" textValue={t("nav.signOut")} variant="danger">
            <ArrowRightFromSquare className="size-4" />
            <Label>{t("nav.signOut")}</Label>
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown>
  );
}

/** Below the md breakpoint the sidebar becomes a drawer behind a menu button. */
function MobileNavigation() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  // Close after navigating.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the path change is the trigger.
  useEffect(() => setOpen(false), [location.pathname]);
  return (
    <>
      <Button variant="ghost" size="sm" isIconOnly aria-label={t("nav.menu")} onPress={() => setOpen(true)}>
        <Bars className="size-5" />
      </Button>
      <Drawer.Backdrop isOpen={open} onOpenChange={setOpen}>
        <Drawer.Content placement="left">
          <Drawer.Dialog className="flex h-full flex-col">
            <Drawer.CloseTrigger />
            <Drawer.Header>
              <Drawer.Heading>
                <Brand />
              </Drawer.Heading>
            </Drawer.Header>
            <Drawer.Body className="flex-1 pt-4">
              <Navigation />
            </Drawer.Body>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    </>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen bg-background">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col gap-8 overflow-y-auto border-r border-border bg-surface px-4 py-6 md:flex">
        <div className="px-3">
          <Brand />
        </div>
        <Navigation />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 flex h-16 items-center gap-2 border-b border-border bg-background/85 px-4 backdrop-blur sm:px-8">
          <div className="flex items-center gap-2 md:hidden">
            <MobileNavigation />
            <Brand compact />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <PreferenceControls />
            <UserMenu />
          </div>
        </header>

        <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 sm:px-8 lg:py-10">{children}</main>
      </div>
    </div>
  );
}
